// src/pages/BasicUpload.js
import React, { useState } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const BasicUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [downloadURL, setDownloadURL] = useState('');
  const [logs, setLogs] = useState([]);

  const addLog = (message) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
      addLog(`File selected: ${e.target.files[0].name}`);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');
    setDownloadURL('');
    addLog('Starting upload...');

    try {
      // Use the absolute most basic storage upload possible
      const storage = getStorage();
      const storageRef = ref(storage, `test-uploads/${file.name}`);
      
      addLog(`Created storage reference: test-uploads/${file.name}`);
      
      // Use the simplest upload method
      addLog('Uploading file...');
      const snapshot = await uploadBytes(storageRef, file);
      addLog('Upload completed!');
      
      // Get the download URL
      const url = await getDownloadURL(snapshot.ref);
      setDownloadURL(url);
      addLog(`Download URL obtained: ${url.substring(0, 50)}...`);
      
      setSuccess(`File uploaded successfully: ${file.name}`);
    } catch (error) {
      console.error('Upload error:', error);
      addLog(`ERROR: ${error.code} - ${error.message}`);
      setError(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      <h1 style={{ marginBottom: '20px' }}>Basic Upload Test</h1>
      <p style={{ marginBottom: '20px' }}>This is a simplified upload component that uses the most basic Firebase Storage methods.</p>
      
      <div style={{ marginBottom: '20px' }}>
        <input 
          type="file" 
          onChange={handleFileChange} 
          disabled={uploading}
          style={{ marginBottom: '10px', display: 'block' }}
        />
        
        <button 
          onClick={handleUpload} 
          disabled={!file || uploading}
          style={{
            backgroundColor: '#128C7E',
            color: 'white',
            border: 'none',
            padding: '10px 15px',
            borderRadius: '4px',
            cursor: !file || uploading ? 'not-allowed' : 'pointer'
          }}
        >
          {uploading ? 'Uploading...' : 'Upload File'}
        </button>
      </div>
      
      {error && (
        <div style={{ 
          backgroundColor: '#ffebee', 
          color: '#c62828', 
          padding: '10px', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}
      
      {success && (
        <div style={{ 
          backgroundColor: '#e8f5e9', 
          color: '#2e7d32', 
          padding: '10px', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <strong>Success:</strong> {success}
        </div>
      )}
      
      {downloadURL && (
        <div style={{
          backgroundColor: '#f5f5f5',
          padding: '15px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <p style={{ marginBottom: '10px' }}>File uploaded successfully:</p>
          <a 
            href={downloadURL} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              color: '#128C7E',
              textDecoration: 'none',
              fontWeight: 'bold'
            }}
          >
            View Uploaded File
          </a>
        </div>
      )}
      
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '4px',
        marginTop: '30px'
      }}>
        <h3 style={{ marginTop: '0', marginBottom: '10px' }}>Upload Logs</h3>
        <div style={{
          backgroundColor: '#000',
          color: '#0f0',
          fontFamily: 'monospace',
          padding: '10px',
          borderRadius: '4px',
          maxHeight: '300px',
          overflow: 'auto'
        }}>
          {logs.length === 0 ? (
            <p>No logs yet. Start an upload to see logs.</p>
          ) : (
            logs.map((log, index) => (
              <div key={index}>{log}</div>
            ))
          )}
        </div>
      </div>
      
    </div>
  );
};

export default BasicUpload;